/**
 * notifyBell.js
 *
 * Drop-in replacement for toast() that routes notifications to the bell
 * instead of floating toast popups.
 *
 * Usage:
 *   import { notifyBell } from "../utils/notifyBell";
 *   notifyBell(uid, { type: "system", title: "Course ready!", message: "...", link: "/..." });
 *
 * For quick transient feedback (save confirmations etc.) still use toast().
 * Use notifyBell() for anything the user might want to reference later.
 */

import {
  collection,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebaseConfig";

/**
 * Write a notification to the user's bell.
 * @param {string} uid - Firebase user UID
 * @param {object} opts
 * @param {string} opts.title       - Short headline
 * @param {string} [opts.message]   - Body text
 * @param {string} [opts.type]      - "system" | "course_available" | "milestone" etc.
 * @param {string} [opts.icon]      - Lucide icon name key (e.g. "AlertCircle", "BookOpen")
 * @param {string} [opts.link]      - Route to navigate to on click
 */
export async function notifyBell(uid, { title, message = "", type = "system", icon = "Info", link = null }) {
  if (!uid || !title) return;
  try {
    await addDoc(collection(db, "users", uid, "notifications"), {
      type,
      title,
      message,
      icon,
      link,
      read: false,
      createdAt: serverTimestamp(),
    });
  } catch (err) {
    console.warn("[notifyBell] Failed to write notification:", err.message);
  }
}

/**
 * Convenience wrappers
 */
export const notifyBellError   = (uid, title, message, link) =>
  notifyBell(uid, { title, message, type: "system", icon: "AlertCircle", link });

export const notifyBellSuccess = (uid, title, message, link) =>
  notifyBell(uid, { title, message, type: "system", icon: "CheckCircle", link });

export const notifyBellInfo    = (uid, title, message, link) =>
  notifyBell(uid, { title, message, type: "system", icon: "Info", link });

export default notifyBell;
